import { useState, lazy, Suspense } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Building2, Users, FileText, ArrowRight, Newspaper, 
  TrendingUp, Play, ChevronRight, Clock, X, Sparkles, Mail
} from "lucide-react";
import { Link } from "wouter";
import { getArticleUrl } from "@/lib/article-url";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { Article, Company, Person } from "@shared/schema";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useSiteContent } from "@/hooks/use-site-content";

const LazyLaunchPanel = lazy(() => import("@/components/special-content-renderer").then(m => ({ default: m.LaunchPanel })));

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek === 1 ? "" : "s"} ago`;
  return `${diffMonth} month${diffMonth === 1 ? "" : "s"} ago`;
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function LaunchPanelFallback() {
  return (
    <div className="animate-pulse">
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  );
}

interface PublicStats {
  totalCompanies: number;
  totalPeople: number;
  totalArticles: number;
  totalRelationships: number;
}

interface ArticleWithHero extends Article {
  heroImageUrl?: string | null;
}

function FeaturedArticleCard({ article, size = "default" }: { article: ArticleWithHero; size?: "large" | "default" | "small" }) {
  const isLarge = size === "large";
  const isSmall = size === "small";
  const imageUrl = article.videoThumbnail || article.heroImageUrl || article.featuredImage;
  const isVideoArticle = article.contentType === "video" || article.contentType === "mixed";
  
  return (
    <Card className={`group hover-elevate overflow-visible ${isLarge ? "row-span-2" : ""}`} data-testid={`card-article-${article.id}`}>
      <Link href={getArticleUrl(article)} data-testid={`link-article-${article.id}`}>
        <div className="cursor-pointer h-full flex flex-col">
          {imageUrl && (
            <div className={`${isLarge ? "aspect-[16/10]" : isSmall ? "aspect-video" : "aspect-[4/3]"} w-full overflow-hidden rounded-t-md relative`}>
              <img 
                src={imageUrl} 
                alt={article.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                data-testid={`img-article-${article.id}`}
              />
              {isVideoArticle && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/90 group-hover:bg-white group-hover:scale-110 transition-all duration-300 flex items-center justify-center shadow-lg">
                    <Play className="w-6 h-6 md:w-7 md:h-7 text-slate-900 ml-0.5" fill="currentColor" />
                  </div>
                </div>
              )}
            </div>
          )}
          {!imageUrl && (
            <div className={`${isLarge ? "aspect-[16/10]" : isSmall ? "aspect-video" : "aspect-[4/3]"} w-full bg-muted rounded-t-md flex items-center justify-center relative`}>
              {isVideoArticle ? (
                <Play className="h-12 w-12 text-muted-foreground" />
              ) : (
                <Newspaper className="h-12 w-12 text-muted-foreground" />
              )}
            </div>
          )}
          <CardHeader className={`${isSmall ? "p-3" : "pb-2"} flex-1`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {isVideoArticle ? (
                <Badge variant="default" className="text-xs bg-red-600 hover:bg-red-700">
                  <Play className="h-2.5 w-2.5 mr-1" />
                  Video
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">
                  News
                </Badge>
              )}
              {article.publishedAt && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatRelativeTime(new Date(article.publishedAt))}
                </span>
              )}
            </div>
            <CardTitle className={`${isLarge ? "text-xl" : isSmall ? "text-sm" : "text-lg"} line-clamp-2`}>
              {article.title}
            </CardTitle>
            {!isSmall && (
              <CardDescription className="line-clamp-2 mt-2">
                {article.excerpt || "No excerpt available"}
              </CardDescription>
            )}
          </CardHeader>
        </div>
      </Link>
    </Card>
  );
}

function CompanyCard({ company }: { company: Company }) {
  return (
    <Card className="hover-elevate">
      <Link href={`/company/${company.slug}`}>
        <div className="cursor-pointer p-4" data-testid={`card-company-${company.id}`}>
          <div className="flex items-center gap-4">
            {company.logo ? (
              <img 
                src={company.logo} 
                alt={company.name}
                loading="lazy"
                className="h-14 w-14 rounded-md object-contain bg-muted p-1"
              />
            ) : (
              <div className="flex h-14 w-14 items-center justify-center rounded-md bg-muted">
                <Building2 className="h-7 w-7 text-muted-foreground" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold truncate">{company.name}</h3>
              <p className="text-sm text-muted-foreground truncate">{company.industry || "Industry"}</p>
              {company.headquarters && (
                <p className="text-xs text-muted-foreground truncate mt-1">{company.headquarters}</p>
              )}
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          </div>
        </div>
      </Link>
    </Card>
  );
}

function VideoArticleCard({ article }: { article: ArticleWithHero }) {
  const imageUrl = article.heroImageUrl || article.videoThumbnail || article.featuredImage;
  const publishedDate = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("en-GB", { month: "short", year: "numeric" })
    : null;

  return (
    <Card className="group hover-elevate overflow-visible" data-testid={`card-video-${article.id}`}>
      <Link href={getArticleUrl(article)}>
        <div className="cursor-pointer">
          <div className="relative aspect-video w-full overflow-hidden rounded-t-md bg-gradient-to-br from-primary/20 to-accent/20">
            {imageUrl && (
              <img
                src={imageUrl}
                alt={article.title}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background/80 backdrop-blur-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <Play className="h-6 w-6 ml-1" />
              </div>
            </div>
            {article.videoMetadataDuration && (
              <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm rounded px-2 py-1 text-xs font-mono">
                {article.videoMetadataDuration}
              </div>
            )}
          </div>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm line-clamp-2">{article.title}</CardTitle>
            {publishedDate && (
              <CardDescription className="text-xs">{publishedDate}</CardDescription>
            )}
          </CardHeader>
        </div>
      </Link>
    </Card>
  );
}

function MagazineCoverCard({ issue }: { issue: number }) {
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const currentMonth = new Date().getMonth();
  const issueMonth = (currentMonth - issue + 12) % 12;
  const year = issue > currentMonth ? 2025 : 2026;
  
  return (
    <Card className="group hover-elevate overflow-visible" data-testid={`card-magazine-issue-${100 - issue}`}>
      <div className="cursor-pointer">
        <div className="relative aspect-[3/4] w-full overflow-hidden rounded-t-md bg-gradient-to-br from-primary via-primary/80 to-accent">
          <div className="absolute inset-0 p-4 flex flex-col justify-between text-primary-foreground">
            <div>
              <div className="text-xs font-medium opacity-80">{months[issueMonth]} {year}</div>
              <div className="text-xl font-bold mt-1">FM World</div>
              <div className="text-xs opacity-80">Magazine</div>
            </div>
            <div>
              <div className="text-sm font-semibold line-clamp-2">
                {issue === 0 ? "The Future of Distribution" : 
                 issue === 1 ? "Supply Chain Innovation" :
                 issue === 2 ? "Industry Leaders Speak" :
                 "Market Trends Report"}
              </div>
            </div>
          </div>
        </div>
        <CardHeader className="p-3">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="outline" className="text-xs">Issue #{100 - issue}</Badge>
            <Button variant="ghost" size="sm" className="text-xs" data-testid={`button-read-issue-${100 - issue}`}>
              Read <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </CardHeader>
      </div>
    </Card>
  );
}

function StatCard({ icon: Icon, value, label, trend }: { icon: any; value: number | string; label: string; trend?: string }) {
  return (
    <div className="text-center p-6 rounded-lg bg-card border">
      <Icon className="mx-auto h-8 w-8 text-primary mb-3" />
      <div className="text-3xl font-bold" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-muted-foreground">{label}</div>
      {trend && (
        <div className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center justify-center gap-1">
          <TrendingUp className="h-3 w-3" /> {trend}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, subtitle, viewAllHref, viewAllText = "View All", testId }: { 
  title?: string; 
  subtitle?: string; 
  viewAllHref?: string;
  viewAllText?: string;
  testId?: string;
}) {
  return (
    <div className="flex items-end justify-between mb-6 gap-4">
      <div>
        {title && <h2 className="text-2xl font-bold">{title}</h2>}
        {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
      </div>
      {viewAllHref && (
        <Button variant="ghost" asChild className="flex-shrink-0" data-testid={testId || `link-view-all-${(title || "").toLowerCase().replace(/\s/g, '-')}`}>
          <Link href={viewAllHref}>
            {viewAllText} <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      )}
    </div>
  );
}

export default function LandingPage() {
  const [showBetaBanner, setShowBetaBanner] = useState(true);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistCompany, setWaitlistCompany] = useState("");
  const { toast } = useToast();
  const { getContent, getContentInfo, getJsonContent } = useSiteContent("home");

  type SC = { visible?: boolean; heading?: string; subtitle?: string; badge?: string; description?: string; cta_primary?: string; cta_secondary?: string; fieldVisible?: Record<string, boolean> };
  const fv = (cfg: SC, key: string) => cfg.fieldVisible?.[key] !== false;
  const secHero           = getJsonContent<SC>("section_hero", {});
  const secLaunchPanel    = getJsonContent<SC>("section_launch_panel", {});
  const secTrendingNow    = getJsonContent<SC>("section_trending_now", {});
  const secIndustryNews   = getJsonContent<SC>("section_industry_news", {});
  const secVideoContent   = getJsonContent<SC>("section_video_content", {});
  const secMagazine       = getJsonContent<SC>("section_magazine", {});
  const secFeaturedCos    = getJsonContent<SC>("section_featured_companies", {});
  const secStats          = getJsonContent<SC>("section_stats", {});
  const secNewsletter     = getJsonContent<SC>("section_newsletter", {});

  const renderContent = (key: string, defaultValue: string, className?: string) => {
    const info = getContentInfo(key, defaultValue);
    if (info.isRichText) {
      return <span className={className} dangerouslySetInnerHTML={{ __html: info.value }} />;
    }
    return <span className={className}>{info.value}</span>;
  };

  const { data: articles, isLoading: articlesLoading } = useQuery<ArticleWithHero[]>({
    queryKey: ["/api/articles/published"],
  });

  const { data: videoArticles } = useQuery<ArticleWithHero[]>({
    queryKey: ["/api/articles/videos"],
  });

  const { data: companies, isLoading: companiesLoading } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
  });
  
  const { data: people } = useQuery<Person[]>({
    queryKey: ["/api/people"],
  });

  const { data: stats } = useQuery<PublicStats>({
    queryKey: ["/api/public/stats"],
  });

  const waitlistMutation = useMutation({
    mutationFn: async (data: { email: string; name?: string; company?: string }) => {
      const res = await apiRequest("POST", "/api/waitlist", data);
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "You're on the list!",
        description: "We'll notify you when we launch.",
      });
      setShowWaitlistModal(false);
      setWaitlistEmail("");
      setWaitlistName("");
      setWaitlistCompany("");
    },
    onError: (error: any) => {
      toast({
        title: "Oops!",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleWaitlistSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    waitlistMutation.mutate({ 
      email: waitlistEmail, 
      name: waitlistName || undefined, 
      company: waitlistCompany || undefined 
    });
  };

  const publishedCompanies = companies?.filter(c => c.isPublished) || [];
  const publishedPeople = people?.filter(p => p.isPublished) || [];
  const featuredArticle = articles?.[0];
  const trendingArticles = articles?.slice(1, 5) || [];
  const latestArticles = articles?.filter(a => a.contentType !== "video" && a.contentType !== "mixed").slice(0, 6) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Beta Banner */}
      {showBetaBanner && (
        <div className="bg-slate-900 text-slate-50 relative">
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="flex items-center justify-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="font-medium">Beta Preview</span>
                <span className="hidden sm:inline">- We're launching soon! Join the waitlist to be first to know.</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="border-slate-600 text-slate-50 hover:bg-slate-800 hover:text-slate-50"
                onClick={() => setShowWaitlistModal(true)}
                data-testid="button-join-waitlist-banner"
              >
                Join Waitlist
              </Button>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Dismiss banner"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 text-slate-300 hover:bg-slate-800 hover:text-slate-50"
            onClick={() => setShowBetaBanner(false)}
            data-testid="button-close-beta-banner"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Header */}
      <SiteHeader />

      <main id="main-content">
      {/* Hero Section with Featured Article */}
      {secHero.visible !== false && <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              {fv(secHero, 'badge') && (
                <Badge variant="secondary" className="bg-white text-slate-900">
                  {getContent("hero_badge", "The Distribution Industry Hub")}
                </Badge>
              )}
              {fv(secHero, 'heading') && (
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight [&_p]:m-0 [&_em]:text-primary [&_em]:not-italic">
                  {renderContent("hero_heading", "Your Source for Industry News")}
                </h1>
              )}
              {fv(secHero, 'description') && (
                <div className="text-lg text-slate-300 max-w-xl [&_p]:m-0">
                  {renderContent("hero_description", "Connect with companies, people, and insights across the distribution industry. Stay informed with breaking news, in-depth analysis, and exclusive interviews.")}
                </div>
              )}
              {(fv(secHero, 'cta_primary') || fv(secHero, 'cta_secondary')) && (
                <div className="flex flex-wrap gap-4">
                  {fv(secHero, 'cta_primary') && (
                    <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100" asChild data-testid="button-explore">
                      <Link href="/explore">
                        {getContent("hero_cta_primary", "Explore Content")} <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                  )}
                  {fv(secHero, 'cta_secondary') && (
                    <Button size="lg" variant="outline" asChild data-testid="button-subscribe">
                      <Link href="/subscribe">
                        {getContent("hero_cta_secondary", "Subscribe Free")}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </div>
            
            {featuredArticle ? (
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white overflow-visible">
                <Link href={getArticleUrl(featuredArticle)}>
                  <div className="cursor-pointer">
                    {(featuredArticle.heroImageUrl || featuredArticle.featuredImage) && (
                      <div className="aspect-video w-full overflow-hidden rounded-t-md">
                        <img 
                          src={featuredArticle.heroImageUrl || featuredArticle.featuredImage || ''} 
                          alt={featuredArticle.title}
                          width={640}
                          height={360}
                          loading="eager"
                          fetchPriority="high"
                          className="h-full w-full object-cover"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-primary text-primary-foreground">Featured</Badge>
                        {featuredArticle.publishedAt && (
                          <span className="text-xs text-slate-300">
                            {formatShortDate(new Date(featuredArticle.publishedAt))}
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-xl text-white">{featuredArticle.title}</CardTitle>
                      <CardDescription className="text-slate-300">
                        {featuredArticle.excerpt || "Read the full story"}
                      </CardDescription>
                    </CardHeader>
                  </div>
                </Link>
              </Card>
            ) : (
              <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white overflow-hidden">
                <div className="aspect-video flex flex-col items-center justify-center bg-slate-800/50 rounded-t-md">
                  <Newspaper className="h-16 w-16 text-slate-400 mb-4" />
                  <p className="text-slate-300 text-sm">Featured content coming soon</p>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>}

      {/* Launch Panel - Special Content */}
      {secLaunchPanel.visible !== false && <section className="py-12 bg-muted/30 border-b">
        <div className="mx-auto max-w-7xl px-4">
          <Suspense fallback={<LaunchPanelFallback />}>
            <LazyLaunchPanel />
          </Suspense>
        </div>
      </section>}

      {/* Trending Now */}
      {secTrendingNow.visible !== false && <section className="py-12 border-b min-h-[400px]">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHeader 
            title={fv(secTrendingNow, 'heading') ? (secTrendingNow.heading || "Trending Now") : undefined}
            subtitle={fv(secTrendingNow, 'subtitle') ? (secTrendingNow.subtitle || "The most read stories this week") : undefined}
            viewAllHref="/articles"
          />
          
          {articlesLoading ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i}>
                  <Skeleton className="aspect-[4/3] w-full rounded-t-md" />
                  <CardHeader>
                    <Skeleton className="h-4 w-20" />
                    <Skeleton className="h-6 w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          ) : trendingArticles.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {trendingArticles.map((article) => (
                <FeaturedArticleCard key={article.id} article={article} size="small" />
              ))}
            </div>
          ) : (
            <Card className="py-12 text-center">
              <Newspaper className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No articles yet. Check back soon!</p>
            </Card>
          )}
        </div>
      </section>}

      {/* Industry News by Category */}
      {secIndustryNews.visible !== false && <section className="py-12 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHeader 
            title={fv(secIndustryNews, 'heading') ? (secIndustryNews.heading || "Industry News") : undefined}
            subtitle={fv(secIndustryNews, 'subtitle') ? (secIndustryNews.subtitle || "Stay updated with the latest from every sector") : undefined}
            viewAllHref="/articles"
          />
          
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Distribution */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-8 bg-primary rounded"></div>
                <h3 className="font-semibold">Distribution</h3>
              </div>
              {latestArticles.slice(0, 2).length > 0 ? (
                <div className="space-y-4">
                  {latestArticles.slice(0, 2).map((article) => (
                    <FeaturedArticleCard key={article.id} article={article} size="small" />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center text-muted-foreground">
                  No articles in this category
                </Card>
              )}
            </div>

            {/* Technology */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-8 bg-accent rounded"></div>
                <h3 className="font-semibold">Technology</h3>
              </div>
              {latestArticles.slice(2, 4).length > 0 ? (
                <div className="space-y-4">
                  {latestArticles.slice(2, 4).map((article) => (
                    <FeaturedArticleCard key={article.id} article={article} size="small" />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center text-muted-foreground">
                  No articles in this category
                </Card>
              )}
            </div>

            {/* Market Trends */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-8 bg-green-500 rounded"></div>
                <h3 className="font-semibold">Market Trends</h3>
              </div>
              {latestArticles.slice(4, 6).length > 0 ? (
                <div className="space-y-4">
                  {latestArticles.slice(4, 6).map((article) => (
                    <FeaturedArticleCard key={article.id} article={article} size="small" />
                  ))}
                </div>
              ) : (
                <Card className="p-6 text-center text-muted-foreground">
                  No articles in this category
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>}

      {/* Video Content */}
      {secVideoContent.visible !== false && videoArticles && videoArticles.length > 0 && (
        <section className="py-12 border-b">
          <div className="mx-auto max-w-7xl px-4">
            <SectionHeader
              title={fv(secVideoContent, 'heading') ? (secVideoContent.heading || "Video Content") : undefined}
              subtitle={fv(secVideoContent, 'subtitle') ? (secVideoContent.subtitle || "Watch interviews, insights, and industry spotlights") : undefined}
              viewAllHref="/videos"
            />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {videoArticles.slice(0, 4).map((article) => (
                <VideoArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FM World Magazine */}
      {secMagazine.visible !== false && <section className="py-12 bg-gradient-to-r from-primary/5 via-background to-accent/5">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHeader 
            title={fv(secMagazine, 'heading') ? (secMagazine.heading || "FM World Magazine") : undefined}
            subtitle={fv(secMagazine, 'subtitle') ? (secMagazine.subtitle || "The voice of the distribution industry") : undefined}
            viewAllHref="/magazine"
            viewAllText="All Issues"
          />
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <MagazineCoverCard key={i} issue={i} />
            ))}
          </div>
        </div>
      </section>}

      {/* Featured Companies */}
      {secFeaturedCos.visible !== false && <section className="py-12 border-b">
        <div className="mx-auto max-w-7xl px-4">
          <SectionHeader 
            title={fv(secFeaturedCos, 'heading') ? (secFeaturedCos.heading || "Featured Companies") : undefined}
            subtitle={fv(secFeaturedCos, 'subtitle') ? (secFeaturedCos.subtitle || "Leaders in the distribution ecosystem") : undefined}
            viewAllHref="/featured-companies"
          />
          
          {companiesLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="h-14 w-14 rounded-md" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : publishedCompanies.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {publishedCompanies.slice(0, 6).map((company) => (
                <CompanyCard key={company.id} company={company} />
              ))}
            </div>
          ) : (
            <Card className="py-12 text-center">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No featured companies yet.</p>
              <p className="text-sm text-muted-foreground mt-2">
                Companies will appear here once they are published.
              </p>
            </Card>
          )}
        </div>
      </section>}

      {/* Statistics Dashboard */}
      {secStats.visible !== false && <section className="py-16 bg-muted/30">
        <div className="mx-auto max-w-7xl px-4">
          <div className="text-center mb-10">
            {fv(secStats, 'heading') && <h2 className="text-2xl font-bold mb-2">{secStats.heading || "Platform at a Glance"}</h2>}
            {fv(secStats, 'subtitle') && <p className="text-muted-foreground">{secStats.subtitle || "The pulse of the distribution industry"}</p>}
          </div>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <StatCard 
              icon={Building2} 
              value={stats?.totalCompanies || 0} 
              label="Companies" 
            />
            <StatCard 
              icon={Users} 
              value={stats?.totalPeople || 0} 
              label="Industry Contacts" 
            />
            <StatCard 
              icon={FileText} 
              value={stats?.totalArticles || 0} 
              label="Published Articles" 
            />
            <StatCard 
              icon={TrendingUp} 
              value={stats?.totalRelationships ?? 0} 
              label="Relationships Tracked" 
            />
          </div>
        </div>
      </section>}

      {/* Newsletter Signup */}
      {secNewsletter.visible !== false && <section className="py-16 bg-slate-900 text-slate-50">
        <div className="mx-auto max-w-7xl px-4">
          <div className="max-w-2xl mx-auto text-center">
            <Mail className="mx-auto h-12 w-12 mb-4 text-primary" />
            {fv(secNewsletter, 'heading') && <h2 className="text-2xl font-bold mb-2">{secNewsletter.heading || "Stay in the Loop"}</h2>}
            {fv(secNewsletter, 'subtitle') && (
              <p className="opacity-80 mb-6">
                {secNewsletter.subtitle || "Get the latest industry news, company updates, and exclusive content delivered to your inbox weekly."}
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input 
                type="email" 
                placeholder="Enter your email" 
                className="bg-white text-slate-900"
                data-testid="input-newsletter-email"
              />
              <Button variant="secondary" className="flex-shrink-0" data-testid="button-newsletter-subscribe">
                Subscribe <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs opacity-60 mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </section>}

      </main>

      {/* Footer */}
      <SiteFooter />

      {/* Waitlist Modal */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Join the Waitlist
            </DialogTitle>
            <DialogDescription>
              Be among the first to access FM World when we launch. We'll send you an exclusive invite.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleWaitlistSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="waitlist-email">Email *</Label>
                <Input
                  id="waitlist-email"
                  type="email"
                  placeholder="your@email.com"
                  value={waitlistEmail}
                  onChange={(e) => setWaitlistEmail(e.target.value)}
                  required
                  data-testid="input-waitlist-email"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="waitlist-name">Name</Label>
                <Input
                  id="waitlist-name"
                  type="text"
                  placeholder="Your name (optional)"
                  value={waitlistName}
                  onChange={(e) => setWaitlistName(e.target.value)}
                  data-testid="input-waitlist-name"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="waitlist-company">Company</Label>
                <Input
                  id="waitlist-company"
                  type="text"
                  placeholder="Your company (optional)"
                  value={waitlistCompany}
                  onChange={(e) => setWaitlistCompany(e.target.value)}
                  data-testid="input-waitlist-company"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowWaitlistModal(false)} data-testid="button-cancel-waitlist">
                Cancel
              </Button>
              <Button type="submit" disabled={waitlistMutation.isPending} data-testid="button-submit-waitlist">
                {waitlistMutation.isPending ? "Joining..." : "Join Waitlist"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
